<template>
  <div class="flex h-screen bg-gray-100">
    <!-- Desktop Sidebar -->
    <aside class="hidden md:flex w-60 bg-white border-l border-gray-200 flex-col flex-shrink-0 shadow-sm">
      <div class="flex items-center gap-3 px-5 h-16 border-b border-gray-200 flex-shrink-0">
        <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
          <i class="pi pi-chart-bar text-white text-sm" />
        </div>
        <div class="flex flex-col">
          <span class="text-gray-900 font-bold text-sm tracking-tight">TimeIt</span>
          <span class="text-gray-400 text-[10px]">Platform Admin</span>
        </div>
      </div>

      <nav class="flex-1 py-4 space-y-1 px-3 overflow-y-auto">
        <button
          v-for="item of navItems"
          :key="item.name"
          :class="navItemClass(item.name)"
          @click="navigate(item.name)"
        >
          <i :class="['pi', item.icon, 'text-sm', currentRoute === item.name ? 'text-primary-600' : '']" />
          <span>{{ item.label }}</span>
        </button>
      </nav>

      <div class="px-3 pb-4 flex-shrink-0">
        <button
          class="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
          @click="logout"
        >
          <i class="pi pi-sign-out text-sm" />
          <span>התנתקות</span>
        </button>
      </div>
    </aside>

    <!-- Mobile Drawer -->
    <Drawer
      v-model:visible="mobileNavOpen"
      position="right"
      :showHeader="false"
      :style="{ width: '260px' }"
      :pt="{ mask: { class: 'md:!hidden' } }"
    >
      <div class="flex flex-col h-full">
        <div class="flex items-center gap-3 px-5 h-16 border-b border-gray-200 flex-shrink-0">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <i class="pi pi-chart-bar text-white text-sm" />
          </div>
          <div class="flex flex-col">
            <span class="text-gray-900 font-bold text-sm tracking-tight">TimeIt</span>
            <span class="text-gray-400 text-[10px]">Platform Admin</span>
          </div>
        </div>

        <nav class="flex-1 py-4 space-y-1 px-3 overflow-y-auto">
          <button
            v-for="item of navItems"
            :key="item.name"
            :class="navItemClass(item.name)"
            @click="navigateAndClose(item.name)"
          >
            <i :class="['pi', item.icon, 'text-sm', currentRoute === item.name ? 'text-primary-600' : '']" />
            <span>{{ item.label }}</span>
          </button>
        </nav>

        <div class="px-3 pb-4 flex-shrink-0">
          <button
            class="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
            @click="logout"
          >
            <i class="pi pi-sign-out text-sm" />
            <span>התנתקות</span>
          </button>
        </div>
      </div>
    </Drawer>

    <!-- Main column -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
      <!-- Mobile top bar -->
      <header class="md:hidden flex items-center justify-between h-14 px-4 bg-white border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <i class="pi pi-chart-bar text-white text-xs" />
          </div>
          <span class="text-gray-900 font-bold text-sm tracking-tight">TimeIt</span>
        </div>
        <Button
          icon="pi pi-bars"
          text
          rounded
          class="!text-gray-600"
          @click="mobileNavOpen = true"
        />
      </header>

      <!-- Main content -->
      <main class="flex-1 overflow-y-auto">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Session from 'supertokens-web-js/recipe/session';
import { PlatformRouteNames } from '../router/route-names';

const router = useRouter();
const route = useRoute();

const currentRoute = computed(() => route.name);
const mobileNavOpen = ref(false);

const navItems = [
  { name: PlatformRouteNames.Activity, icon: 'pi-chart-bar', label: 'סקירת פעילות' },
  { name: PlatformRouteNames.Analytics, icon: 'pi-chart-line', label: 'אנליטיקות' },
  { name: PlatformRouteNames.Crm, icon: 'pi-users', label: 'לידים ומשימות' },
];

const navItemClass = (name: PlatformRouteNames) => [
  'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm transition-all duration-200',
  currentRoute.value === name
    ? 'bg-primary-50 text-primary-700 font-medium border border-primary-200'
    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50',
];

const navigate = (name: PlatformRouteNames) => {
  router.push({ name });
};

const navigateAndClose = (name: PlatformRouteNames) => {
  navigate(name);
  mobileNavOpen.value = false;
};

const logout = async () => {
  await Session.signOut();
  router.push({ name: PlatformRouteNames.Login });
};
</script>
